import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormControl,
  FormGroup,
  AbstractControl,
} from '@angular/forms';

import { OAuthProvider } from 'appwrite';
import {
  cardAnimation,
  formSlideInAnimation,
} from '../../../shared/animations/animations';
import { AuthenticationService } from '../../../shared/services';
import { AppwriteService } from '../../../shared/services/appwrite.sdk.service';
import { environment } from '../../../../environments/environment';
import { emailRegex, passwordRegex } from '../../../shared/services/regex';
import {
  BetaInvitationValidationResponse,
  RegisterModel,
} from '../../../models/register';
import { lastValueFrom, throwError } from 'rxjs';

export enum RegisterErrorCode {
  username_exist = 602,
  email_exist = 609,
}

export enum CurrentRegistrationStep {
  capture = 'capture',
  register = 'register',
}
@Component({
  selector: 'app-user-register-form',
  templateUrl: './user-register-form.component.html',
  styleUrls: ['./user-register-form.component.css'],
  animations: [formSlideInAnimation, cardAnimation],
})
export class UserRegisterFormComponent implements OnInit, OnChanges {
  OAuthProvider = OAuthProvider;
  loading: boolean = false;

  isOAuthEnabled: boolean = false;

  currentRegistrationStep = CurrentRegistrationStep;
  currentStep: CurrentRegistrationStep = CurrentRegistrationStep.capture;

  @Input() invitation: BetaInvitationValidationResponse | null = null;
  @Output() onLoadingStart = new EventEmitter<any>();
  @Output() onLoadingDone = new EventEmitter<any>();
  @Output() onError = new EventEmitter<any>();
  passwordInputType: 'password' | 'text' = 'password';

  private trackingCode: string | null = null;

  validationObj: { isEmailExist: boolean; isUsernameExist: boolean } = {
    isEmailExist: false,
    isUsernameExist: false,
  };

  signupForm = new FormGroup({
    leadGroup: new FormGroup({
      firstname: new FormControl('', [Validators.required]),
      lastname: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(emailRegex),
      ]),
    }),
    registrationGroup: new FormGroup({
      // username: new FormControl('', [Validators.required]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(passwordRegex),
      ]),
      toc: new FormControl(false, [this.requiredTrueValidator]),
    }),
  });

  constructor(
    private authenticationService: AuthenticationService,
    public appwriteService: AppwriteService
  ) {
    this.isOAuthEnabled = environment.appwrite.oAuthEnabled;
  }

  ngOnInit() {
    let emailControl = this.signupForm.get('leadGroup').get('email');
    emailControl.valueChanges.subscribe((value) => {
      if (emailControl.hasError('isExist')) {
        emailControl.hasError = undefined;
      }
    });

    this.applyInvitation(this.invitation);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['invitation']) {
      this.applyInvitation(changes['invitation'].currentValue);
    }
  }

  private applyInvitation(invitation: BetaInvitationValidationResponse | null) {
    if (!invitation) {
      return;
    }

    this.trackingCode = invitation.trackingCode;
    this.signupForm.get('leadGroup').patchValue({
      firstname: invitation.firstName,
      lastname: invitation.lastName,
      email: invitation.email,
    });
  }

  isCurrentStep(step: CurrentRegistrationStep): boolean {
    return this.currentStep === step;
  }

  // Custom validator for "I Agree" checkbox
  requiredTrueValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    if (!control?.value) {
      return { requiredTrue: true };
    }
    return null;
  }

  togglePasswordVisibility() {
    if (this.passwordInputType == 'password') {
      this.passwordInputType = 'text';
    } else {
      this.passwordInputType = 'password';
    }
  }

  async captureLead(payload: Partial<RegisterModel>) {
    const request$ = this.authenticationService.captureLead(
      payload,
      this.handleCaptureLeadError
    );
    await lastValueFrom(request$);
  }

  handleCaptureLeadError = (error: any) => {
    const statusCode = error?.statusCode ?? error?.error?.statusCode;
    switch (statusCode) {
      case 609:
        this.validationObj.isEmailExist = true;
        this.signupForm
          .get('leadGroup')
          .get('email')
          .setErrors({ isExist: true });
        break;
      default:
        break;
    }
    this.onSignupError(error);
    return throwError(() => error);
  };

  async signUp(registerModel: Partial<RegisterModel>) {
    this.onSignupStart();
    let request = this.authenticationService.register(
      registerModel,
      this.handleRegisterError
    );
  }

  handleRegisterError = (error: {
    error: string;
    message: string;
    statusCode: number;
  }) => {
    switch (error?.statusCode) {
      case 609:
        this.validationObj.isEmailExist = true;
        this.signupForm
          .get('leadGroup')
          .get('email')
          .setErrors({ isExist: true });
        break;
      case 602:
        this.signupForm
          .get('registrationGroup')
          .get('username')
          .setErrors({ isExist: true });
        break;

      default:
        break;
    }
    this.onSignupError(error);
  };

  onSubmit() {
    if (this.isCurrentStep(CurrentRegistrationStep.capture)) {
      this.submitCaptureStep();
    } else if (this.isCurrentStep(CurrentRegistrationStep.register)) {
      this.submitRegisterStep();
    }
  }

  private async submitCaptureStep() {
    this.signupForm.controls.leadGroup.markAllAsTouched();

    if (!this.signupForm.controls.leadGroup.valid) {
      return;
    }

    // Invitation validate already created the user on the backend, so
    // users/is-registered-before would fail (email exists). Skip it and
    // move to the password step; registration still ties via trackingCode.
    if (this.trackingCode) {
      this.currentStep = CurrentRegistrationStep.register;
      return;
    }

    const lead = this.signupForm.controls.leadGroup.getRawValue();
    const payload: Partial<RegisterModel> = {
      firstname: lead.firstname,
      lastname: lead.lastname,
      email: lead.email,
    };

    try {
      await this.captureLead(payload);
      this.currentStep = CurrentRegistrationStep.register;
    } catch {
      // Errors handled in handleCaptureLeadError; stay on capture step.
    }
  }

  private submitRegisterStep() {
    this.signupForm.controls.registrationGroup.markAllAsTouched();
    this.logFormErrors(this.signupForm);

    if (
      this.signupForm.controls.leadGroup.valid &&
      this.signupForm.controls.registrationGroup.valid
    ) {
      const lead = this.signupForm.controls.leadGroup.getRawValue();
      const registration = this.signupForm.controls.registrationGroup.getRawValue();
      const payload: Partial<RegisterModel> = {
        firstname: lead.firstname,
        lastname: lead.lastname,
        email: lead.email,
        password: registration.password,
        confirmPassword: registration.password,
      };

      if (this.trackingCode) {
        payload.trackingCode = this.trackingCode;
      }

      this.signUp(payload);
    }
  }

  logFormErrors(formGroup: FormGroup | FormControl, prefix: string = '') {
    let errors: any[] = [];
    if (formGroup instanceof FormGroup) {
      for (const controlName in formGroup.controls) {
        const control = formGroup.get(controlName);

        if (control instanceof FormGroup || control instanceof FormControl) {
          this.logFormErrors(control, `${prefix}${controlName}.`);
        }
      }
    } else if (formGroup instanceof FormControl && formGroup.errors) {
      errors.push(formGroup.errors);
    }
  }

  onSignupStart() {
    this.onLoadingStart.emit();
  }

  onSignupDone(resp) {
    setTimeout(() => {
      this.onLoadingDone.emit();
    }, 500);
  }

  onSignupError(error: { error: string; message: string; statusCode: number }) {
    this.onError.emit();
  }
}
